import Button from 'tailwind-components/Button/Button';
import CollapsibleList from 'components/CollapsibleList/CollapsibleList';
import CollapsibleSection from 'components/CollapsibleList/CollapsibleSection';
import LoadingContent from 'components/LoadingContent/LoadingContent';
import React, { useState, useMemo } from 'react';
import propTypes from 'prop-types';
import style from './mainSectionCarriers.module.scss';
import { ChevronUp, ChevronDown, Info } from 'lucide-react';
import { Divider } from '@mui/material';
import {
  getCarrierDisplayName,
  isAgentAppointedWithCarrier
} from 'helpers/appointments/appointments';
import { getCompany } from 'helpers/getCompanyVariables';
import { useSelector } from 'react-redux';
import CarrierCard from 'tailwind-components/CarrierCard/CarrierCard';
import CarrierCardNoStates from 'tailwind-components/CarrierCardNoStates/CarrierCardNoStates';
import ICHRASection from './ICHRASection';
import CarrierSectionIconLegend from 'components/CarrierSectionIconLegend/CarrierSectionIconLegend';
import {
  collectSectionCarrierIconLegends,
  sectionHasUplineDisabledCarriers
} from 'helpers/carriers/carrierSectionIconLegends';
import { isSuncareContext } from 'helpers/suncare/suncare';

const company = getCompany();

function getExpandedState(simplified) {
  const url = String(window.location).toLowerCase();

  const states = {
    '#marketplace': 'marketplace',
    '#ancillary': 'ancillary',
    '#ichra': !simplified && 'ichra'
  };

  for (const [hash, key] of Object.entries(states)) {
    if (key && url.endsWith(hash)) {
      return {
        marketplace: false,
        ancillary: false,
        ichra: false,
        [key]: true
      };
    }
  }

  return { marketplace: company !== 'oneill', ancillary: false, ichra: false };
}

function getMixedCarrierCards(carriers, user) {
  return carriers.some(
    carrier =>
      (carrier.enableStateRequestsIfAppointed
        ? isAgentAppointedWithCarrier(carrier.name, user)
        : carrier.considerStates === 'Yes' &&
          carrier.statesOffered.length > 0) &&
      (!carrier.appointmentsDisabled ||
        isAgentAppointedWithCarrier(carrier.name, user))
  );
}

function groupCarriersByType(carriers) {
  return carriers.reduce((groups, carrier) => {
    const type = carrier.type ?? 'Other';
    if (!groups[type]) groups[type] = [];
    groups[type].push(carrier);
    return groups;
  }, {});
}

function MainSectionCarriers({
  carriers,
  active,
  setActive = () => {},
  Card,
  CardNoStates,
  header,
  message = 'No Appointed Carriers',
  localLoader,
  simplified = false
}) {
  const CardComponent = Card ?? CarrierCard;
  const CardNoStatesComponent = CardNoStates ?? CarrierCardNoStates;

  const { marketplaceCarriers, offMarketCarriers } = useMemo(() => {
    let marketplace = Object.values(carriers.Marketplace);
    let offMarket = Object.values(carriers['Off-Market Health']);

    if (company === 'oneill') {
      const enrollPrime = offMarket.find(c => c.name === 'Enroll Prime');
      if (enrollPrime) {
        marketplace = [...marketplace, enrollPrime];
        offMarket = offMarket.filter(c => c !== enrollPrime);
      }
    }

    const sortByName = (a, b) =>
      getCarrierDisplayName(a.name).localeCompare(
        getCarrierDisplayName(b.name)
      );

    return {
      marketplaceCarriers: [...marketplace].sort(sortByName),
      offMarketCarriers: [...offMarket].sort(sortByName)
    };
  }, [carriers]);

  const user = useSelector(state => state.user.zohoData);
  const { actionableAppointments } = useSelector(state => state.appointments);

  const [expandedState, setExpandedState] = useState(
    getExpandedState(simplified)
  );
  const expandedSome = Object.values(expandedState).some(Boolean);

  const healthIconLegends = useMemo(
    () => collectSectionCarrierIconLegends(marketplaceCarriers, user),
    [marketplaceCarriers, user]
  );

  const healthUplineDisabled = useMemo(
    () => sectionHasUplineDisabledCarriers(marketplaceCarriers, user),
    [marketplaceCarriers, user]
  );

  const ancillaryIconLegends = useMemo(
    () => collectSectionCarrierIconLegends(offMarketCarriers, user),
    [offMarketCarriers, user]
  );

  const ancillaryUplineDisabled = useMemo(
    () => sectionHasUplineDisabledCarriers(offMarketCarriers, user),
    [offMarketCarriers, user]
  );

  // --- Helpers ---

  function renderCarrierCard(
    carrier,
    i,
    { isMixed = false, withDashedBorder = false, adjustHeight = false } = {}
  ) {
    const isAppointed = isAgentAppointedWithCarrier(carrier.name, user);

    if (carrier.appointmentsDisabled && !isAppointed) return null;

    const considerStates =
      (carrier.enableStateRequestsIfAppointed
        ? isAppointed
        : carrier.considerStates === 'Yes' &&
          carrier.statesOffered.length > 0) && !isSuncareContext(carrier.name);

    const sharedProps = {
      key: `${carrier.name} ${i}`,
      ...carrier,
      active,
      setActive,
      carrierActionableAppts: actionableAppointments.filter(
        appt => appt.carrier === carrier.name
      )
    };

    return considerStates ? (
      <CardComponent {...sharedProps} isMixed={isMixed} />
    ) : (
      <CardNoStatesComponent
        {...sharedProps}
        adjustHeight={adjustHeight}
        dashedBorder={withDashedBorder}
      />
    );
  }

  function renderCarrierGroup(
    carriersToRender,
    { isMixed = false, getDashedBorder } = {}
  ) {
    const hasMixed = getMixedCarrierCards(carriersToRender, user);
    const groups = groupCarriersByType(carriersToRender);
    const groupEntries = Object.entries(groups);

    return groupEntries.map(([type, groupCarriers], groupIndex) => (
      <div className={style.myAppointmentsSection} key={type}>
        {groupIndex > 0 && (
          <Divider textAlign='left' sx={{ width: '100%', my: 2 }}>
            {type === 'Marketplace'
              ? 'ACA'
              : type === 'Off-Market Health'
              ? 'Off-Market'
              : type}
          </Divider>
        )}
        {groupIndex === 0 && groupEntries.length > 1 && (
          <Divider textAlign='left' sx={{ width: '100%', mb: 2 }}>
            {type === 'Marketplace'
              ? 'ACA'
              : type === 'Off-Market Health'
              ? 'Off-Market'
              : type}
          </Divider>
        )}
        <div className={style.children}>
          {groupCarriers.map((carrier, i) =>
            renderCarrierCard(carrier, i, {
              isMixed,
              adjustHeight: hasMixed,
              withDashedBorder: getDashedBorder?.(carrier) ?? false
            })
          )}
        </div>
      </div>
    ));
  }

  // --- Render ---

  if (localLoader) {
    return (
      <div className={style.MainSectionCarriers}>
        <LoadingContent />
      </div>
    );
  }

  if (marketplaceCarriers.length === 0 && offMarketCarriers.length === 0) {
    return (
      <div className={style.MainSectionCarriers}>
        <div className={style.notice}>{message}</div>
      </div>
    );
  }

  return (
    <div className={style.MainSectionCarriers}>
      <CollapsibleList
        options={
          <Button
            outlined
            size='xsmall'
            text={expandedSome ? 'Collapse all' : 'Expand all'}
            variant='default'
            icon={
              expandedSome ? <ChevronUp size={24} /> : <ChevronDown size={24} />
            }
            onClick={() =>
              setExpandedState(() => {
                return {
                  marketplace: !expandedSome,
                  ancillary: !expandedSome,
                  ichra: !expandedSome
                };
              })
            }
          />
        }
      >
        {header}

        <CollapsibleSection
          title={
            company === 'oneill'
              ? 'Health Appointments'
              : 'Marketplace Appointments'
          }
          titleAlert={
            company === 'oneill' && !simplified ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: '#3fa8ff'
                }}
              >
                <Info size={16} />
                <span style={{ marginTop: '3px' }}>New Product Offerings</span>
              </div>
            ) : null
          }
          id='MarketplaceAppointments'
          subtitle={
            company === 'oneill'
              ? 'Affordable Care Act and Off-Market Carriers'
              : 'Affordable Care Act Carriers'
          }
          titleTrailing={
            <CarrierSectionIconLegend
              legends={healthIconLegends}
              showUplineDisabled={healthUplineDisabled}
            />
          }
          arrowRight
          expanded={expandedState.marketplace}
          onClick={() =>
            setExpandedState({
              ...expandedState,
              marketplace: !expandedState.marketplace
            })
          }
        >
          {marketplaceCarriers.length > 0 ? (
            <>
              {renderCarrierGroup(marketplaceCarriers, {
                getDashedBorder: carrier =>
                  carrier.name === 'Enroll Prime' && company === 'oneill'
              })}
              {company === 'oneill' && (
                <p
                  style={{
                    display: 'block',
                    marginTop: '20px',
                    marginBottom: '0px',
                    opacity: 0.7
                  }}
                >
                  <strong>Note:</strong>{' '}
                  <span>
                    By enrolling with <strong>Enroll Prime</strong>, you gain
                    access to a full catalog of products.
                  </span>
                </p>
              )}
            </>
          ) : (
            'No marketplace appointments available'
          )}
        </CollapsibleSection>

        {user.agencyData.Hide_Off_Market === false && (
          <CollapsibleSection
            title={
              company === 'oneill'
                ? 'Ancillary Appointments'
                : 'Off-Market Health Appointments'
            }
            id='OffMarketHealthAppointments'
            subtitle='Includes STM, Indemnity, Accident, CI, Dental, and Vision Carriers.'
            titleTrailing={
              <CarrierSectionIconLegend
                legends={ancillaryIconLegends}
                showUplineDisabled={ancillaryUplineDisabled}
              />
            }
            arrowRight
            expanded={expandedState.ancillary}
            onClick={() =>
              setExpandedState({
                ...expandedState,
                ancillary: !expandedState.ancillary
              })
            }
          >
            {offMarketCarriers.length > 0
              ? renderCarrierGroup(offMarketCarriers, { isMixed: true })
              : 'No Off-Market Health appointments available'}
          </CollapsibleSection>
        )}

        {company === 'oneill' && !simplified && (
          <ICHRASection
            expandedState={expandedState}
            setExpandedState={setExpandedState}
          />
        )}
      </CollapsibleList>
    </div>
  );
}

MainSectionCarriers.propTypes = {
  carriers: propTypes.object,
  active: propTypes.string,
  setActive: propTypes.func,
  Card: propTypes.func,
  CardNoStates: propTypes.func,
  header: propTypes.element,
  message: propTypes.string,
  localLoader: propTypes.bool,
  simplified: propTypes.bool
};

export default MainSectionCarriers;
